import { useQuery as useTanstackQuery } from '@tanstack/react-query'
import qs from 'qs'
import { useCallback } from 'react'
import { apiBaseUrl, apiEndpoint } from '~/constants/api'
import { useAuthStore } from '~/stores/auth'
import type { Todo } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { QueryKey } from '~/types/query-key'
import { useToast } from './use-toast'

type UseQueryOptions<TData> = {
  queryKey: QueryKey
  idParams?: Todo
  queries?: Record<string, Todo>
  withToken?: boolean
  options?: {
    enabled?: boolean
    staleTime?: number
    refetchOnWindowFocus?: boolean
  }
  onSuccess?: (data: TData) => void
}

async function refreshTokens(accessToken: string, refreshToken: string) {
  const url = `${apiBaseUrl}${apiEndpoint.mutation[MutationKey.REFRESH_TOKEN]()}`
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken, refreshToken }),
  })

  if (!response.ok) {
    throw new Error('Token refresh failed')
  }

  return response.json()
}

export function useQuery<TData = Todo>({
  queryKey,
  idParams,
  queries,
  withToken = true,
  options,
  onSuccess,
}: UseQueryOptions<TData>) {
  const { accessToken, refreshToken, setTokens, clearTokens } = useAuthStore()
  const { showError } = useToast()

  const queryFn = useCallback(async (): Promise<TData> => {
    const endpoint = apiEndpoint.query[queryKey](idParams)
    const queryString = queries ? `?${qs.stringify(queries, { arrayFormat: 'brackets' })}` : ''
    const url = `${apiBaseUrl}${endpoint}${queryString}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (withToken && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    let response = await fetch(url, { headers })

    if (response.status === 401 && withToken && refreshToken) {
      try {
        const data = await refreshTokens(accessToken, refreshToken)
        setTokens(data.tokens.accessToken, data.tokens.refreshToken)

        headers['Authorization'] = `Bearer ${data.tokens.accessToken}`
        response = await fetch(url, { headers })
      } catch {
        clearTokens()
        showError('Session expired, please login again')
        throw new Error('Token refresh failed')
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const message = errorData.message || 'Something went wrong'
      showError(message)
      throw new Error(message)
    }

    const data = await response.json()

    if (onSuccess) {
      onSuccess(data)
    }

    return data
  }, [queryKey, idParams, queries, withToken, accessToken, refreshToken, setTokens, clearTokens, showError, onSuccess])

  return useTanstackQuery<TData>({
    queryKey: [queryKey, queries, idParams],
    queryFn,
    enabled: withToken ? !!accessToken : true,
    ...options,
  })
}
